import {
  BadgeCheck, Building2, CalendarClock, ClipboardList, FlaskConical, KeyRound, LifeBuoy,
  Receipt, ServerCrash, ShieldAlert, Stethoscope, Tent, UserPlus, type LucideIcon,
} from 'lucide-react';

/** What a notification is about. Mirrors the API's notification catalogue. */
export type NotificationCategory =
  | 'account' | 'checkup' | 'appointment' | 'result' | 'certificate' | 'compliance'
  | 'company' | 'billing' | 'case' | 'review' | 'camp' | 'lead' | 'support' | 'system';

export const CATEGORY: Record<NotificationCategory, { label: string; icon: LucideIcon; tone: string }> = {
  account: { label: 'Account', icon: KeyRound, tone: 'bg-neutral-100 text-neutral-600' },
  checkup: { label: 'Checkups', icon: Stethoscope, tone: 'bg-primary-50 text-primary-700' },
  appointment: { label: 'Appointments', icon: CalendarClock, tone: 'bg-info-100 text-info-600' },
  result: { label: 'Results', icon: FlaskConical, tone: 'bg-primary-50 text-primary-700' },
  certificate: { label: 'Certificates', icon: BadgeCheck, tone: 'bg-success-100 text-success-600' },
  compliance: { label: 'Compliance', icon: ShieldAlert, tone: 'bg-warning-100 text-warning-600' },
  company: { label: 'Companies', icon: Building2, tone: 'bg-primary-50 text-primary-700' },
  billing: { label: 'Billing', icon: Receipt, tone: 'bg-success-100 text-success-600' },
  case: { label: 'Cases', icon: ClipboardList, tone: 'bg-info-100 text-info-600' },
  review: { label: 'Reviews', icon: Stethoscope, tone: 'bg-accent-50 text-accent-700' },
  camp: { label: 'Camps', icon: Tent, tone: 'bg-warning-100 text-warning-600' },
  lead: { label: 'Leads', icon: UserPlus, tone: 'bg-accent-50 text-accent-700' },
  support: { label: 'Support', icon: LifeBuoy, tone: 'bg-info-100 text-info-600' },
  system: { label: 'System', icon: ServerCrash, tone: 'bg-danger-100 text-danger-600' },
};

export const categoryOf = (c: string | undefined | null) =>
  CATEGORY[(c ?? 'system') as NotificationCategory] ?? CATEGORY.system;

/** Fired on window when the bell sees an arrival or something is marked read. */
export const NOTIFICATIONS_CHANGED = 'dru:notifications-changed';

const holds = (perms: ReadonlySet<string>, p: string) =>
  perms.has(p) || perms.has(`${p.split('.')[0]}.*`) || perms.has('*');

/** HR rows written before links existed: where their subject lives. */
const ENTITY: Record<string, { base: string; need: string }> = {
  Employee: { base: '/hr/employees', need: 'employee.read' },
  Appointment: { base: '/hr/appointments', need: 'appointment.read' },
  FitnessCertificate: { base: '/hr/certificates', need: 'certificate.read' },
  Campaign: { base: '/hr/campaigns', need: 'campaign.read' },
  ExaminationOrder: { base: '/hr/examinations', need: 'examination.read' },
  Invoice: { base: '/hr/billing', need: 'invoice.read' },
};

/**
 * Where a notification opens, in the console the reader is in. A venue's
 * link is written once ("/partner/queue") and read by a hospital, a lab or
 * a venue that is both, so it is translated; a link into another console is
 * not offered.
 */
export function notificationTarget(
  n: { linkPath: string | null; entityType?: string | null; entityId?: string | null },
  prefix: string,
  permissions: ReadonlySet<string>,
  /** Other consoles this login can open; a link into one of them is offered too. */
  reachable: string[] = [],
): string | null {
  let link = n.linkPath;
  if (link?.startsWith('/partner/')) {
    if (prefix === '/lab') link = link === '/partner/queue' ? '/lab/worklist' : null;
    else if (prefix === '/hospital') link = link === '/partner/queue' ? '/hospital/queue' : null;
  }
  if (link === '/admin/leads' && prefix === '/ops') link = '/ops/leads';
  if (link && [prefix, ...reachable].some((p) => link === p || link!.startsWith(`${p}/`))) return link;
  if (prefix === '/hr' && !n.linkPath && n.entityType) {
    const e = ENTITY[n.entityType];
    if (e && holds(permissions, e.need)) return n.entityId ? `${e.base}/${n.entityId}` : e.base;
  }
  return null;
}
